import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import LoadError from "@/components/LoadError";
import { ShieldCheck, Play, Plus, LinkIcon, Film, BarChart3, Archive, Trash2, Check, AlertCircle, ClipboardList } from "lucide-react";
import MeetingViewer from "@/pages/safety/MeetingViewer";
import ComplianceReport from "@/pages/safety/ComplianceReport";
import AddMeetingForm from "@/pages/safety/AddMeetingForm";

export default function SafetyMeetings({ canManage = false, canViewReport = false }) {
    const location = useLocation();
    const rolePrefix = location.pathname.startsWith("/foreman") ? "/foreman" : "/admin";
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [adding, setAdding] = useState(false);
    const [viewer, setViewer] = useState(null);
    const [report, setReport] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await api.get(`/safety/meetings?include_archived=${canManage && showArchived}`);
            setMeetings(r.data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Couldn’t load safety meetings.");
        } finally {
            setLoading(false);
        }
    }, [canManage, showArchived]);

    useEffect(() => { load(); }, [load]);

    const markWatched = (id) => setMeetings((ms) => ms.map((m) => (m.meeting_id === id ? { ...m, watched_by_me: true } : m)));

    const archive = async (m) => {
        await api.patch(`/safety/meetings/${m.meeting_id}`, { archived: !m.archived });
        load();
    };
    const remove = async (m) => {
        if (!window.confirm(`Delete "${m.title}"? This also removes its watch records.`)) return;
        await api.delete(`/safety/meetings/${m.meeting_id}`);
        load();
    };

    if (err && !meetings.length) return <LoadError message={err} onRetry={load} testId="safety-meetings-error" />;

    return (
        <div className="max-w-3xl mx-auto px-4 py-4 space-y-4 pb-28" data-testid="safety-meetings-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-bold text-2xl text-brand-charcoal flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-brand-navy" /> Safety Meetings</h1>
                    <p className="text-sm text-brand-charcoal/60">Watch the presentation, then check off that you saw it.</p>
                </div>
                {canManage && !adding && (
                    <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-brand-admin-header hover:bg-[#6E1824] text-brand-bone font-bold text-sm shrink-0" data-testid="add-meeting-button">
                        <Plus className="h-4 w-4" /> Add
                    </button>
                )}
            </div>

            {canManage && adding && <AddMeetingForm onCreated={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />}

            <div className="flex items-center justify-between flex-wrap gap-2">
                {canManage && (
                    <label className="flex items-center gap-2 text-sm text-brand-charcoal/70" data-testid="show-archived-toggle">
                        <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4" />
                        Show archived
                    </label>
                )}
                {canViewReport && (
                    <Link to={`${rolePrefix}/safety/compliance-report`} className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-navy hover:underline" data-testid="compliance-rollup-link">
                        <ClipboardList className="h-4 w-4" /> Compliance report (date range)
                    </Link>
                )}
            </div>

            {loading ? (
                <div className="p-8 text-center text-brand-charcoal/60" data-testid="safety-loading">Loading…</div>
            ) : meetings.length === 0 ? (
                <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-8 text-center text-brand-charcoal/60" data-testid="safety-empty">
                    No safety meetings yet.{canManage ? " Tap Add to post your first one." : " Check back Monday."}
                </div>
            ) : (
                <ul className="space-y-3" data-testid="safety-meetings-list">
                    {meetings.map((m) => (
                        <li key={m.meeting_id} className={`bg-white rounded-2xl border shadow-card p-4 ${m.archived ? "border-brand-charcoal/10 opacity-70" : "border-brand-charcoal/15"}`} data-testid={`meeting-${m.meeting_id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-brand-navy">
                                            {m.source_type === "video" ? <Film className="h-3 w-3" /> : <LinkIcon className="h-3 w-3" />}
                                            {m.source_type === "video" ? "Video" : "Canva"}
                                        </span>
                                        {m.archived && <span className="text-[10px] uppercase tracking-widest font-bold text-brand-charcoal/40">Archived</span>}
                                    </div>
                                    <div className="font-bold text-brand-charcoal text-lg leading-tight mt-1 truncate" data-testid={`meeting-title-${m.meeting_id}`}>{m.title}</div>
                                    <div className="text-xs text-brand-charcoal/50 mt-0.5">Posted {dayjs(m.created_at).format("ddd, MMM D")} · {m.created_by_name}</div>
                                </div>
                                {m.watched_by_me ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-success bg-brand-success/10 px-2.5 py-1 rounded-full shrink-0" data-testid={`watched-pill-${m.meeting_id}`}><Check className="h-3.5 w-3.5" /> Watched</span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-danger bg-brand-danger/10 px-2.5 py-1 rounded-full shrink-0" data-testid={`notwatched-pill-${m.meeting_id}`}><AlertCircle className="h-3.5 w-3.5" /> Not watched</span>
                                )}
                            </div>

                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <button onClick={() => setViewer(m)} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-brand-navy hover:bg-brand-navy-dark text-brand-bone font-bold text-sm" data-testid={`watch-button-${m.meeting_id}`}>
                                    <Play className="h-4 w-4" /> {m.watched_by_me ? "Watch again" : "Watch"}
                                </button>
                                {canViewReport && (
                                    <button onClick={() => setReport(m)} className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl bg-white border border-brand-charcoal/20 text-brand-charcoal font-bold text-sm hover:bg-brand-stone" data-testid={`report-button-${m.meeting_id}`}>
                                        <BarChart3 className="h-4 w-4" /> {m.ack_count ?? 0} watched
                                    </button>
                                )}
                                {canManage && (
                                    <>
                                        <button onClick={() => archive(m)} className="inline-flex items-center gap-1 h-10 px-3 rounded-xl bg-white border border-brand-charcoal/20 text-brand-charcoal/70 font-semibold text-sm hover:bg-brand-stone" data-testid={`archive-button-${m.meeting_id}`}>
                                            <Archive className="h-4 w-4" /> {m.archived ? "Unarchive" : "Archive"}
                                        </button>
                                        <button onClick={() => remove(m)} className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white border border-brand-charcoal/20 text-brand-danger hover:bg-brand-danger/10" data-testid={`delete-button-${m.meeting_id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {viewer && <MeetingViewer meeting={viewer} onClose={() => setViewer(null)} onAcked={markWatched} />}
            {report && <ComplianceReport meetingId={report.meeting_id} title={report.title} onClose={() => setReport(null)} />}
        </div>
    );
}
